import type { PanelBlock, PanelRow } from '../../types'

const HALF_RAIL_DIVISOR = 2
const MAX_OVERHANG = 300
const OVERHANG_BUFFER = 100

const CABLE_CLIPS_PER_4700 = 3
const CABLE_CLIPS_PER_2400 = 2

export type FootTypeKey =
  | 'L-Feet'
  | 'Tile Hook'
  | 'Full size Kliplok Bracket'
  | 'Mini Kliplok Bracket'
  | 'Longline'
  | 'Trap Bracket'

export const BRACKET_FOOT_TYPES: FootTypeKey[] = [
  'Full size Kliplok Bracket',
  'Mini Kliplok Bracket',
  'Longline',
  'Trap Bracket',
]

export interface PerSetResult {
  fullSticks: number
  joiners: number
  feet: number
  endClamps: number
  midClamps: number
  roofBrackets: number
  extraFraction: number
  totalPanels: number
  totalRows: number
  steps: {
    runLength: number
    overhangRemainder: number
    overhangTriggered: boolean
    adjustedRailLength: number
    roundedRailLength: number
  }
}

export interface BomTotals {
  railsTotal: number
  joinersTotal: number
  feetTotal: number
  endClampsTotal: number
  midClampsTotal: number
  roofBracketsTotal: number
  earthingLugsTotal: number
  endCapsTotal: number
  panelClips: number
  cableClips: number
  arcRubber: number
  flushLFeet: number
  totalRows: number
}

export function computePerSet(
  panelsPerRow: number,
  numRows: number,
  panelSpacing: number,
  footSpacing: number,
  maxOverhang: number,
  railIncrement: number,
  channelLength: number,
  extraLength: number
): PerSetResult {
  const runLength = panelsPerRow * panelSpacing + extraLength
  const overhangRemainder = runLength % footSpacing
  const overhangTooLarge = overhangRemainder > maxOverhang
  const adjusted = overhangTooLarge ? runLength - overhangRemainder + footSpacing + OVERHANG_BUFFER : runLength
  const rounded = Math.ceil(adjusted / railIncrement) * railIncrement

  const fullSticks = numRows * Math.floor(rounded / channelLength)
  const joiners = numRows * (Math.ceil(rounded / channelLength) - 1)
  const feet = numRows * (Math.floor(rounded / footSpacing) + 1)
  const endClamps = numRows * 2
  const midClamps = (panelsPerRow - 1) * numRows
  const roofBrackets = feet
  const extraFraction = numRows * ((rounded % channelLength) / channelLength)

  return {
    fullSticks,
    joiners,
    feet,
    endClamps,
    midClamps,
    roofBrackets,
    extraFraction,
    totalPanels: panelsPerRow * numRows,
    totalRows: numRows,
    steps: {
      runLength,
      overhangRemainder,
      overhangTriggered: overhangTooLarge,
      adjustedRailLength: adjusted,
      roundedRailLength: rounded,
    },
  }
}

export function extractSetsFromRows(rows: PanelRow[]): { panelsPerRow: number; numRows: number }[] {
  if (!rows.length) return []

  const sets: { panelsPerRow: number; numRows: number }[] = []
  let currentCount = -1
  let currentNumRows = 0

  for (const row of rows) {
    const activePanels = row.filter((p) => p.isActive).length
    if (activePanels === 0) continue
    if (activePanels === currentCount) {
      currentNumRows++
    } else {
      if (currentCount > 0) {
        sets.push({ panelsPerRow: currentCount, numRows: currentNumRows })
      }
      currentCount = activePanels
      currentNumRows = 1
    }
  }
  if (currentCount > 0) {
    sets.push({ panelsPerRow: currentCount, numRows: currentNumRows })
  }
  return sets
}

export function getContiguousColumns(rows: PanelRow[]): PanelRow[] {
  if (!rows.length) return []

  const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0)
  const result: PanelRow[] = []

  for (let col = 0; col < columnCount; col++) {
    let currentGroup: PanelRow[number][] = []
    for (const row of rows) {
      const panel = row[col]
      if (panel?.isActive) {
        currentGroup.push(panel)
      } else {
        if (currentGroup.length > 0) {
          result.push(currentGroup)
          currentGroup = []
        }
      }
    }
    if (currentGroup.length > 0) {
      result.push(currentGroup)
    }
  }

  return result
}

export function computeAllSetsTotals(
  allSets: { panelsPerRow: number; numRows: number }[],
  panelSpacing: number,
  footSpacing: number,
  channelLength: number,
  extraLength: number,
  channels: number,
  corrugated: boolean,
  footType: FootTypeKey,
  tiltOption: string = 'No tilt'
): BomTotals {
  const railIncrement = channelLength / HALF_RAIL_DIVISOR
  const maxOverhang = MAX_OVERHANG

  let totalFullSticks = 0
  let totalJoiners = 0
  let totalFeet = 0
  let totalEndClamps = 0
  let totalMidClamps = 0
  let totalRoofBrackets = 0
  let totalExtraFraction = 0
  let totalPanels = 0
  let totalRows = 0
  for (const set of allSets) {
    const r = computePerSet(
      set.panelsPerRow,
      set.numRows,
      panelSpacing,
      footSpacing,
      maxOverhang,
      railIncrement,
      channelLength,
      extraLength
    )
    totalFullSticks += r.fullSticks
    totalJoiners += r.joiners
    totalFeet += r.feet
    totalEndClamps += r.endClamps
    totalMidClamps += r.midClamps
    totalRoofBrackets += r.roofBrackets
    totalExtraFraction += r.extraFraction
    totalPanels += r.totalPanels
    totalRows += r.totalRows
  }

  const railsTotal = totalFullSticks * channels + Math.ceil(totalExtraFraction * channels)
  const joinersTotal = totalJoiners * channels
  const feetTotal = totalFeet * channels
  const endClampsTotal = totalEndClamps * channels
  const midClampsTotal = totalMidClamps * channels
  const roofBracketsTotal = totalRoofBrackets * channels
  const earthingLugsTotal = totalRows * channels
  const endCapsTotal = totalRows * channels
  const panelClips = 4 * totalPanels
  const arcRubber = corrugated && footType === 'L-Feet' ? roofBracketsTotal : 0

  const qty4700 = channelLength === 4700 ? railsTotal : 0
  const qty2400 = channelLength === 2400 ? railsTotal : 0
  const cableClips = CABLE_CLIPS_PER_4700 * qty4700 + CABLE_CLIPS_PER_2400 * qty2400
  const flushLFeet = tiltOption === 'No tilt' && BRACKET_FOOT_TYPES.includes(footType) ? roofBracketsTotal : 0

  return {
    railsTotal,
    joinersTotal,
    feetTotal,
    endClampsTotal,
    midClampsTotal,
    roofBracketsTotal,
    earthingLugsTotal,
    endCapsTotal,
    panelClips,
    cableClips,
    arcRubber,
    flushLFeet,
    totalRows,
  }
}

export {
  getDeviceCountOnRailForBlocks,
  getGridUuidsFromBlocks,
  getStrungModuleCountForBlocks,
  getStringCountForBlocks,
  getSystemFromBlocks,
} from '../../mlpeBlockUtils'
